import React, { useEffect, useMemo, useState } from 'react';
import MiniMapPlayer, { PlayerRosterCard } from './MiniMapPlayer';
import ProductionTrack from './ProductionTrack';
import LossesTrack from './LossesTrack';
import IntelArrivalsTrack from './IntelArrivalsTrack';
import TrackHoverTip from './TrackHoverTip';
import MacroIssuesTable from './MacroIssuesTable';
import MacroCard from './MacroCard';
import KeyBattlesCard from './KeyBattlesCard';
import FuturePanel from './FuturePanel';
import CameraStage from './CameraStage';
import { needsProductionTrackReparse } from '../parsedGameFreshness';
import {
  normalizeCameraPids,
  colorForPlayer,
  entitiesAtTime,
  formatClock,
  inProgressIntervals,
  lossesAtTime,
  nextPlayheadSpeed,
  rosterForPlayer,
  statsAtTime,
  upgradesAtTime,
} from '../replayPlayerState';
import { formatIntervalTooltip } from '../productionTrackRows';
import { upgradeLabel } from '../upgradeDisplay';
import './ReplayPlayer.css';

function groupLosses(losses) {
  const counts = new Map();
  for (const loss of losses) {
    const key = loss.name || 'Unknown';
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

function TrackHoverBanner({ hover, emptyHint }) {
  if (!hover?.text) {
    return <p className="rp-track-hover muted" data-testid="side-track-hover">{emptyHint}</p>;
  }
  return (
    <p className="rp-track-hover" data-testid="side-track-hover">
      {hover.text}
    </p>
  );
}

function ProductionPanel({ intervals, time, playerName, hover, onSeek }) {
  const inProgress = inProgressIntervals(intervals, time);
  const hoverIsProd = hover?.kind === 'production';
  return (
    <div className="rp-production-panel" data-testid="production-panel">
      <h4>Production{playerName ? ` · ${playerName}` : ''}</h4>
      <TrackHoverBanner
        hover={hoverIsProd ? hover : null}
        emptyHint="Hover a Production Track bar for name, span, and cost."
      />
      {inProgress.length === 0 ? (
        <p className="rp-obs-muted">Nothing in progress at this Playhead.</p>
      ) : (
        <ul className="rp-production-list">
          {inProgress.map((item, idx) => {
            const tip = formatIntervalTooltip(item);
            return (
              <li key={`${item.name}-${item.start_real_time}-${idx}`}>
                <button
                  type="button"
                  className="rp-production-row"
                  onClick={() => onSeek?.(item.start_real_time || 0)}
                >
                  {tip}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function LossesPanel({ losses, playerName, onSeek, hover }) {
  const grouped = groupLosses(losses);
  const hoverIsLoss = hover?.kind === 'loss';
  return (
    <div className="rp-losses" data-testid="losses-panel">
      <h4>Losses{playerName ? ` · ${playerName}` : ''}</h4>
      <TrackHoverBanner
        hover={hoverIsLoss ? hover : null}
        emptyHint="Hover a Losses Track cell for composition and cost."
      />
      {grouped.length === 0 ? (
        <p className="rp-obs-muted">No losses at this Playhead (or timelines missing).</p>
      ) : (
        <ul className="rp-losses-list">
          {grouped.map((item) => {
            const first = losses.find((l) => l.name === item.name);
            return (
              <li key={item.name}>
                <button
                  type="button"
                  className="rp-loss-row"
                  onClick={() => first && onSeek?.(first.t)}
                >
                  <span>{item.name}</span>
                  <span>×{item.count}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="macro-issues-hint">Click a loss type to seek its first death.</p>
    </div>
  );
}

function ObserverCard({
  playerMeta,
  buildOrder,
  roster,
  time,
  selected,
  onSelect,
}) {
  const analysis = buildOrder?.player_analysis;
  const apm = analysis?.basic_info?.apm ?? 0;
  const upgrades = upgradesAtTime(analysis?.upgrades_completed, time);
  const inProgress = inProgressIntervals(buildOrder?.production_intervals, time);
  const name = playerMeta?.name || buildOrder?.player || 'Player';

  return (
    <button
      type="button"
      className={`rp-obs-card${selected ? ' active' : ''}`}
      data-testid={`observer-${name}`}
      aria-pressed={selected}
      onClick={() => onSelect?.(name)}
      style={{ '--obs-accent': colorForPlayer(playerMeta) }}
    >
      <div className="rp-obs-head">
        <span
          className="rp-obs-swatch"
          style={{ backgroundColor: colorForPlayer(playerMeta) }}
        />
        <strong className="rp-obs-name">{name}</strong>
        <span className="rp-obs-race">{playerMeta?.race || analysis?.basic_info?.race || ''}</span>
        <span className="rp-obs-apm">APM {Math.round(apm)}</span>
      </div>
      <div className="rp-obs-stats">
        <span data-testid={`obs-workers-${name}`}>Workers {roster?.workers ?? 0}</span>
        <span data-testid={`obs-army-${name}`}>
          Army{' '}
          <span className="rp-m">{roster?.cost?.minerals ?? 0}</span>
          {' / '}
          <span className="rp-g">{roster?.cost?.gas ?? 0}</span>
        </span>
      </div>
      <div className="rp-obs-upgrades" data-testid={`obs-upgrades-${name}`}>
        {upgrades.length === 0 ? (
          <span className="rp-obs-muted">No upgrades yet</span>
        ) : (
          upgrades.map((u) => {
            const label = upgradeLabel(u.name);
            return (
              <span
                key={`${u.name}-${u.time}`}
                className={`rp-upg rp-upg-${label.category}`}
                title={label.full}
              >
                {label.short}
                {u.count > 1 ? `×${u.count}` : ''}
              </span>
            );
          })
        )}
      </div>
      <div className="rp-obs-prod" data-testid={`obs-prod-${name}`}>
        <span className="rp-obs-muted">In progress</span>
        {inProgress.length === 0 ? (
          <span className="rp-obs-muted">—</span>
        ) : (
          <div className="rp-prod-q">
            {inProgress.slice(0, 6).map((item, idx) => {
              const start = item.start_real_time || 0;
              const finish = item.finish_real_time || start;
              const span = Math.max(0.001, finish - start);
              const fill = Math.min(1, Math.max(0, (time - start) / span));
              return (
                <div key={`${item.name}-${start}-${idx}`} className="rp-prod-cell" title={item.name}>
                  <div className="rp-prod-fill" style={{ height: `${fill * 100}%` }} />
                  <span>{String(item.name || '?').slice(0, 3)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * Replay Player — SC2 HUD chrome (ADR 0011). Owns Playhead.
 */
const ReplayPlayer = ({
  minimap,
  buildOrders = [],
  selectedPlayer,
  onSelectedPlayerChange,
  onForceReparse,
  parseLoading = false,
  isAuthenticated = false,
  playerTimelines = null,
  cameras = null,
  replayId = null,
  apiBaseUrl = null,
  schemaVersion = 0,
  mapFileHash = null,
  /** Optional ref filled with (t) => void so sibling surfaces can seek the Playhead. */
  playheadSeekRef = null,
}) => {
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(8);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(true);
  const [macroOpen, setMacroOpen] = useState(true);
  const [macroCardOpen, setMacroCardOpen] = useState(true);
  const [keyBattlesOpen, setKeyBattlesOpen] = useState(true);
  const [productionOpen, setProductionOpen] = useState(true);
  const [troopsOpen, setTroopsOpen] = useState(true);
  const [lossesOpen, setLossesOpen] = useState(true);
  /** Shared Production/Losses horizontal zoom (Fit → 8×). */
  const [trackZoomIndex, setTrackZoomIndex] = useState(0);
  const [trackScrollLeft, setTrackScrollLeft] = useState(0);
  /** @type {[{ kind?: string, text?: string, clientX?: number, clientY?: number }|null, function]} */
  const [trackHover, setTrackHover] = useState(null);

  const players = minimap?.players || [];

  const camerasForMinimap = useMemo(
    () => normalizeCameraPids(cameras, players),
    [cameras, players]
  );

  const selectedBuildOrder = useMemo(
    () => buildOrders.find((bo) => bo.player === selectedPlayer) || null,
    [buildOrders, selectedPlayer]
  );

  const selectedMeta = useMemo(
    () => players.find((p) => p.name === selectedPlayer) || null,
    [players, selectedPlayer]
  );

  const needsReparse = needsProductionTrackReparse(selectedBuildOrder, schemaVersion);
  const intervals = selectedBuildOrder?.production_intervals;
  const duration = Math.max(
    minimap?.duration || 0,
    ...((intervals || []).map((i) => i.finish_real_time || 0)),
    0
  );

  const entities = useMemo(
    () => entitiesAtTime(minimap?.events || [], time),
    [minimap, time]
  );

  const selectedRoster = useMemo(() => {
    if (!selectedMeta) return null;
    return rosterForPlayer(entities, selectedMeta);
  }, [entities, selectedMeta]);

  const observerCards = useMemo(() => {
    const names =
      players.length > 0
        ? players.map((p) => p.name)
        : buildOrders.map((bo) => bo.player);
    return names.map((name) => {
      const meta = players.find((p) => p.name === name) || { name, pid: 0, color: '', race: '' };
      const buildOrder = buildOrders.find((bo) => bo.player === name) || null;
      const roster = rosterForPlayer(entities, meta);
      return { name, meta, buildOrder, roster };
    });
  }, [players, buildOrders, entities]);

  const selectedTimeline = useMemo(() => {
    if (!playerTimelines || !selectedMeta) return null;
    return (
      playerTimelines[String(selectedMeta.pid)] ||
      Object.values(playerTimelines).find((t) => t.name === selectedPlayer) ||
      null
    );
  }, [playerTimelines, selectedMeta, selectedPlayer]);

  const liveStats = useMemo(
    () => statsAtTime(selectedTimeline?.stats, time),
    [selectedTimeline, time]
  );

  const liveLosses = useMemo(() => {
    const raw = selectedTimeline?.losses;
    if (!raw) return null;
    return lossesAtTime(raw, time);
  }, [selectedTimeline, time]);

  const playerColors = useMemo(() => {
    const colors = {};
    for (const p of players) {
      colors[p.name] = colorForPlayer(p);
    }
    for (const bo of buildOrders) {
      if (!colors[bo.player]) colors[bo.player] = colorForPlayer({ name: bo.player });
    }
    return colors;
  }, [players, buildOrders]);

  useEffect(() => {
    if (!playing || duration <= 0) return undefined;
    let raf = 0;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      setTime((prev) => {
        const next = prev + dt * speed;
        if (next >= duration) {
          setPlaying(false);
          return duration;
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, duration]);

  const seek = (t) => {
    setPlaying(false);
    setTime(Math.max(0, Math.min(duration || 0, t)));
  };

  useEffect(() => {
    if (!playheadSeekRef) return undefined;
    playheadSeekRef.current = seek;
    return () => {
      playheadSeekRef.current = null;
    };
  });

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.target?.isContentEditable) return;
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        setPlaying((p) => !p);
        return;
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        setSpeed((s) => nextPlayheadSpeed(s, 1));
        return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        setSpeed((s) => nextPlayheadSpeed(s, -1));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const reparseBlock = (title) => (
    <div className="replay-player-empty">
      <h4>
        {title}
        {selectedPlayer ? ` · ${selectedPlayer}` : ''}
      </h4>
      <p>
        {title} needs a fresh parse. Older ParsedGames lack Production
        Intervals, classification, or the current schema version.
      </p>
      {isAuthenticated && onForceReparse && (
        <button
          type="button"
          className="replay-player-btn"
          onClick={onForceReparse}
          disabled={parseLoading}
        >
          {parseLoading ? 'Re-parsing…' : 'Force re-parse'}
        </button>
      )}
      {!isAuthenticated && (
        <p className="replay-player-hint">Sign in to force re-parse.</p>
      )}
    </div>
  );

  return (
    <div
      className={`rp-shell${sideOpen ? '' : ' side-collapsed'}`}
      data-testid="replay-player-shell"
    >
      <div className="replay-player hud" data-testid="replay-player-hud">
        <button
          type="button"
          className="rp-side-toggle"
          aria-expanded={sideOpen}
          aria-controls="replay-player-side"
          aria-label={sideOpen ? 'Hide side column' : 'Show side column'}
          onClick={() => setSideOpen((open) => !open)}
        >
          <span aria-hidden="true">{sideOpen ? '›' : '‹'}</span>
        </button>
        <div className="rp-top">
          <h3 className="rp-title">Replay Player</h3>
          {liveStats ? (
              <div className="rp-resource-bar" data-testid="resource-bar-live">
                <span className="rp-res m">
                  Minerals <b>{liveStats.minerals ?? 0}</b>
                </span>
                <span className="rp-res g">
                  Gas <b>{liveStats.gas ?? 0}</b>
                </span>
                <span className="rp-res">
                  Income{' '}
                  <b>
                    {liveStats.minerals_rate ?? 0}/{liveStats.gas_rate ?? 0}
                  </b>
                </span>
                <span className="rp-res">
                  Supply{' '}
                  <b>
                    {Math.round(liveStats.food_used ?? 0)}/
                    {Math.round(liveStats.food_made ?? 0)}
                  </b>
                </span>
              </div>
            ) : (
              <FuturePanel
                title="Resources"
                className="rp-resource-future"
              >
                Economy HUD needs player_timelines from a fresh parse.
              </FuturePanel>
            )}
          </div>

          <div className="rp-obs-row" role="group" aria-label="Observer players">
            {observerCards.map((card) => (
              <ObserverCard
                key={card.name}
                playerMeta={card.meta}
                buildOrder={card.buildOrder}
                roster={card.roster}
                time={time}
                selected={selectedPlayer === card.name}
                onSelect={onSelectedPlayerChange}
              />
            ))}
            {observerCards.length === 0 && (
              <div className="rp-obs-empty">No players in this ParsedGame.</div>
            )}
          </div>

          <div className="rp-center">
            <CameraStage
              minimap={minimap}
              cameras={camerasForMinimap}
              cameraPid={selectedMeta?.pid ?? null}
              time={time}
              apiBaseUrl={apiBaseUrl}
              mapFileHash={mapFileHash}
              onHoverChange={setTrackHover}
            />

            {needsReparse ? (
              reparseBlock('Production Track')
            ) : (
              <ProductionTrack
                intervals={intervals || []}
                duration={duration}
                time={time}
                playing={playing}
                onSeek={seek}
                playerName={selectedPlayer}
                zoomIndex={trackZoomIndex}
                onZoomIndexChange={setTrackZoomIndex}
                scrollLeft={trackScrollLeft}
                onScrollLeftChange={setTrackScrollLeft}
                onHoverChange={setTrackHover}
              />
            )}
            {apiBaseUrl && replayId ? (
              <LossesTrack
                apiBaseUrl={apiBaseUrl}
                replayId={replayId}
                playerColors={playerColors}
                duration={duration}
                time={time}
                onSeek={seek}
                zoomIndex={trackZoomIndex}
                scrollLeft={trackScrollLeft}
                onScrollLeftChange={setTrackScrollLeft}
                onHoverChange={setTrackHover}
              />
            ) : null}
            {apiBaseUrl && replayId ? (
              <IntelArrivalsTrack
                apiBaseUrl={apiBaseUrl}
                replayId={replayId}
                player={selectedPlayer}
                duration={duration}
                time={time}
                onSeek={seek}
                zoomIndex={trackZoomIndex}
                scrollLeft={trackScrollLeft}
                onScrollLeftChange={setTrackScrollLeft}
                onHoverChange={setTrackHover}
              />
            ) : null}
            <TrackHoverTip
              text={trackHover?.text}
              x={trackHover?.clientX}
              y={trackHover?.clientY}
            />
          </div>

          <div className="rp-playhead">
            <div className="replay-player-controls">
              <button
                type="button"
                className="replay-player-btn"
                onClick={() => setPlaying((p) => !p)}
              >
                {playing ? 'Pause' : 'Play'}
              </button>
              <button
                type="button"
                className="replay-player-btn secondary"
                onClick={() => {
                  setPlaying(false);
                  setTime(0);
                }}
              >
                Reset
              </button>
              <label className="replay-player-speed">
                Speed
                <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
                  <option value={1}>1x</option>
                  <option value={4}>4x</option>
                  <option value={8}>8x</option>
                  <option value={16}>16x</option>
                  <option value={32}>32x</option>
                </select>
              </label>
              <span className="replay-player-clock" data-testid="playhead-clock">
                {formatClock(time)} / {formatClock(duration)}
              </span>
            </div>
            <input
              className="replay-player-scrubber"
              type="range"
              min={0}
              max={duration || 1}
              step={0.1}
              value={Math.min(time, duration || 0)}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Replay scrubber"
            />
          </div>

          <div className="rp-console">
            <div className="rp-console-minimap">
              <MiniMapPlayer
                minimap={minimap}
                time={time}
                playing={playing}
                speed={speed}
                onTimeChange={setTime}
                onPlayingChange={setPlaying}
                onSpeedChange={setSpeed}
                hideControls
                compact
                cameras={camerasForMinimap}
                cameraPid={selectedMeta?.pid ?? null}
              />
            </div>
            <FuturePanel title="Selected Units" className="rp-selected-units">
              Portrait and unit vitals are not modeled in ParsedGame yet.
            </FuturePanel>
            <FuturePanel title="Command Card" className="rp-command-card">
              Command abilities require selection data (deferred).
            </FuturePanel>
          </div>

          {selectedBuildOrder?.game_events?.length > 0 && (
            <details
              className="replay-player-events"
              open={eventsOpen}
              onToggle={(e) => setEventsOpen(e.target.open)}
            >
              <summary>Events for {selectedPlayer}</summary>
              <div className="replay-player-events-list">
                {selectedBuildOrder.game_events.map((event, index) => (
                  <div key={index} className="replay-player-event-row">
                    <span>{formatClock(event.start_real_time)}</span>
                    <span>{event.supply}</span>
                    <span>{event.name}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <aside
          id="replay-player-side"
          className="rp-side"
          data-testid="replay-player-side"
          hidden={!sideOpen}
        >
          <details
            className="rp-side-section"
            open={macroOpen}
            onToggle={(e) => setMacroOpen(e.target.open)}
            data-testid="side-macro-issues"
          >
            <summary>
              Macro Issues{selectedPlayer ? ` · ${selectedPlayer}` : ''}
            </summary>
            {needsReparse ? (
              reparseBlock('Macro Issues')
            ) : apiBaseUrl && replayId ? (
              <MacroIssuesTable
                apiBaseUrl={apiBaseUrl}
                replayId={replayId}
                player={selectedPlayer}
                playerName={selectedPlayer}
                onSeek={seek}
              />
            ) : (
              <p className="macro-issues-empty">Macro Issues need the facts API.</p>
            )}
          </details>

          {apiBaseUrl && replayId ? (
            <details
              className="rp-side-section"
              open={macroCardOpen}
              onToggle={(e) => setMacroCardOpen(e.target.open)}
              data-testid="side-macro"
            >
              <summary>Macro{selectedPlayer ? ` · ${selectedPlayer}` : ''}</summary>
              <MacroCard
                apiBaseUrl={apiBaseUrl}
                replayId={replayId}
                player={selectedPlayer}
                onSeek={seek}
              />
            </details>
          ) : null}

          {apiBaseUrl && replayId ? (
            <details
              className="rp-side-section"
              open={keyBattlesOpen}
              onToggle={(e) => setKeyBattlesOpen(e.target.open)}
              data-testid="side-key-battles"
            >
              <summary>Key Battles</summary>
              <KeyBattlesCard
                apiBaseUrl={apiBaseUrl}
                replayId={replayId}
                onSeek={seek}
              />
            </details>
          ) : null}

          {selectedRoster ? (
            <details
              className="rp-side-section"
              open={troopsOpen}
              onToggle={(e) => setTroopsOpen(e.target.open)}
              data-testid="side-troops"
            >
              <summary>
                Troops{selectedPlayer ? ` · ${selectedPlayer}` : ''}
              </summary>
              <TrackHoverBanner
                hover={trackHover?.kind === 'camera' ? trackHover : null}
                emptyHint="Hover or click a building on Camera for its name."
              />
              <div className="rp-roster-wrap">
                <PlayerRosterCard roster={selectedRoster} />
              </div>
            </details>
          ) : null}

          {!needsReparse ? (
            <details
              className="rp-side-section"
              open={productionOpen}
              onToggle={(e) => setProductionOpen(e.target.open)}
              data-testid="side-production"
            >
              <summary>
                Production{selectedPlayer ? ` · ${selectedPlayer}` : ''}
              </summary>
              <ProductionPanel
                intervals={intervals || []}
                time={time}
                playerName={selectedPlayer}
                hover={trackHover}
                onSeek={seek}
              />
            </details>
          ) : null}

          {liveLosses ? (
            <details
              className="rp-side-section"
              open={lossesOpen}
              onToggle={(e) => setLossesOpen(e.target.open)}
              data-testid="side-losses"
            >
              <summary>
                Losses{selectedPlayer ? ` · ${selectedPlayer}` : ''}
              </summary>
              <LossesPanel
                losses={liveLosses}
                playerName={selectedPlayer}
                onSeek={seek}
                hover={trackHover}
              />
            </details>
          ) : null}
        </aside>
    </div>
  );
};

export default ReplayPlayer;
